import { LoadingBar, LoadingDots, Modal } from '@flos/react-ui';
import { Suspense, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { topConfigContextAtom } from '../../recoil/atoms/topConfigContextAtom';
import { useErrorBoundary } from 'react-error-boundary';

const LoadingModal = ({
    loadingTitle,
    loadingText,
    showModal,
    closeModal,
    backgroundTask,
    onFinished = () => {}
}: {
    loadingTitle: string;
    loadingText: string;
    showModal: boolean;
    closeModal: () => void;
    backgroundTask: () => Promise<void>;
    onFinished?: () => void;
}): React.JSX.Element => {
    const [progress, setProgress] = useState(0);
    const [completed, setCompleted] = useState(false);
    const topConfigContext = useRecoilValue(topConfigContextAtom);
    const { showBoundary } = useErrorBoundary();

    useEffect(() => {
        const fakeLoadingProgress = setInterval(() => {
            const quickProgressOne = 15;
            const quickProgressTwo = 25;
            const quickProgressThree = 45;
            const slowDownThreshold = 75;
            const infiniteLoadingThreshold = 95;

            const progressAddition = progress > slowDownThreshold ? 2 : 4;
            const newProgress = progress + progressAddition;

            /*
            1. Loading animation should start quickly.
            2. Gradually slow down as it nears 100%.
            3. The last 20% is reserved for infinite loading.
            4. Finish the full animation in 1-2s, when background task is completed.
            5. Optional: Execute a function when the loading is completed e.g. a navigation.
            */
            if (progress === 100) {
                onFinished();
                clearInterval(fakeLoadingProgress);
                closeModal();
            } else if (completed) {
                setProgress(100);
            } else if (newProgress >= infiniteLoadingThreshold) {
                setProgress(infiniteLoadingThreshold);
            } else if (newProgress < quickProgressOne) {
                setProgress(quickProgressOne);
            } else if (newProgress < quickProgressTwo) {
                setProgress(quickProgressTwo);
            } else if (newProgress < quickProgressThree) {
                setProgress(quickProgressThree);
            } else {
                setProgress(newProgress);
            }
        }, 1500);

        return () => {
            clearInterval(fakeLoadingProgress);
        };
    }, [progress, completed, onFinished, closeModal]);

    useEffect(() => {
        const startTime = Date.now();

        const runBackgroundTask = async (): Promise<void> => {
            try {
                await backgroundTask();
            } catch (error) {
                showBoundary(error);
            }
            const endTime = Date.now();
            const elapsedTime = endTime - startTime;

            // Let the animation take at least 2 seconds to avoid flashing the modal.
            if (elapsedTime < 2000) {
                setTimeout(() => {
                    setCompleted(true);
                }, 2000 - elapsedTime);
            } else {
                setCompleted(true);
            }
        };

        void runBackgroundTask();
    }, [backgroundTask, showBoundary]);

    return (
        <Suspense fallback={<LoadingDots />}>
            <Modal isOpen={showModal} onDismiss={() => {}} position={'center'}>
                <style>{`
                    .image-size {
                        width: 400px;
                    }
                    @media (max-width: 991px) {
                        .image-size {
                            width: 193px;
                        }
                    }
                    `}</style>
                <div
                    className={
                        // eslint-disable-next-line max-len
                        'u-display-flex u-flex-column u-flex-column-reverse u-flex-row@md u-gap-none u-gap-7@md u-spacing-inset-right-3 u-spacing-inset-right-9@md u-spacing-inset-left-3 u-spacing-inset-left-9@md'
                    }
                    style={{ alignItems: 'center' }}
                >
                    <div
                        className={'u-Center u-display-flex u-flex-column u-flex-items-center'}
                        style={{ flexGrow: 1 }}
                    >
                        <h3>{loadingTitle}</h3>
                        <p className={'u-spacing-stack-bottom-7'}>{loadingText}</p>
                        <div style={{ width: 150 }}>
                            <LoadingBar progress={progress} />
                        </div>
                    </div>
                    <picture>
                        <source
                            media="(min-width: 1200px),(-webkit-min-device-pixel-ratio: 1.5),(min-resolution: 1.5dppx)"
                            srcSet={`${topConfigContext?.resourceUrl ?? ''}/images/loaderImage.gif`}
                        />
                        <img
                            className={'image-size'}
                            alt="Animated character waiting for the computer to load"
                            src={`${topConfigContext?.resourceUrl ?? ''}/images/loaderImage.gif`}
                        />
                    </picture>
                </div>
            </Modal>
        </Suspense>
    );
};

export default LoadingModal;
